<template>
  <!-- While a puzzle is open the play screen draws its own single bar (title,
       clock, menu, home) - two stacked bars pushed the grid, and the
       keyboard under it, lower than a thumb likes (Matt, 2026-10-09). -->
  <header v-if="!puzzle" class="app-header">
    <h1>Crossword</h1>
    <!-- Nothing in here is usable from behind the gate, so the controls
         stay inside it too. The title stays; the controls go. -->
    <div v-if="isApproved" class="app-header__right">
      <AdminPanel v-if="isAdmin" />
      <button class="icon-button" title="How to play" aria-label="How to play" @click="showHowTo = true">?</button>
    </div>
  </header>

  <SiteGate v-if="!isApproved" />
  <div v-else-if="puzzleLoading" class="screen"><p class="counter-band">Opening the puzzle&hellip;</p></div>
  <PlayScreen v-else-if="puzzle" @home="goHome" @howto="showHowTo = true" />
  <HomeScreen v-else />

  <p v-if="puzzleError" class="notice notice--bad app-error" @click="$store.commit('setPuzzleError', null)">{{ puzzleError }}</p>

  <HowToPlay v-if="showHowTo" @close="dismissHowTo" />

  <!-- Tap reloads the app (an installed PWA has no reload button); press
       and hold is the viewport measurement, a debugging tool. -->
  <p
    class="build-stamp"
    role="button"
    tabindex="0"
    title="Tap to reload · hold to measure"
    @click="stampTap"
    @pointerdown="stampHold"
    @pointerup="stampRelease"
    @pointerleave="stampRelease"
    @pointercancel="stampRelease"
  >{{ measured || (refreshing ? 'reloading…' : stamp) }}</p>
  <BugReportButton v-if="firebaseConfigured" />
  <BugResolutionNotice v-if="firebaseConfigured" />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import PlayScreen from '@/components/puzzle/PlayScreen.vue';
import HomeScreen from '@/components/home/HomeScreen.vue';
import SiteGate from '@/components/auth/SiteGate.vue';
import AdminPanel from '@/components/auth/AdminPanel.vue';
import HowToPlay from '@/components/shared/HowToPlay.vue';
import BugReportButton from '@/components/shared/BugReportButton.vue';
import BugResolutionNotice from '@/components/shared/BugResolutionNotice.vue';
import { buildStamp, forceRefresh } from '@/utils/buildStamp';
import { viewportLine } from '@/utils/viewport';
import { initUpdateCheck } from '@/utils/checkForUpdate';
import { openablePuzzle } from '@/utils/shareResult';
import { localDate } from '@/utils/dates';

const HOWTO_SEEN_KEY = 'crossword:howto-seen';

export default {
  name: 'App',
  components: {
    PlayScreen, HomeScreen, SiteGate, AdminPanel, BugReportButton, BugResolutionNotice, HowToPlay,
  },
  data() {
    return {
      stamp: buildStamp(),
      measured: '',
      refreshing: false,
      held: false,
      showHowTo: false,
      pendingJoinCode: null,
      pendingPuzzle: null,
    };
  },
  computed: {
    ...mapState(['puzzle', 'puzzleLoading', 'puzzleError', 'firebaseConfigured']),
    ...mapGetters(['isApproved', 'isAdmin']),
  },
  watch: {
    // A shared link (?join=FROG) drops someone straight into the room the
    // moment they're signed in and approved.
    isApproved(approved) {
      if (approved) this.consumeJoinCode();
    },
  },
  created() {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (joinCode) this.pendingJoinCode = joinCode;
    // A shared time (?puzzle=2026-10-19) opens that puzzle; never a future one.
    else this.pendingPuzzle = openablePuzzle(params.get('puzzle'), localDate());
  },
  mounted() {
    this.$store.dispatch('initAuth');

    // First-ever visit: open the walkthrough unprompted. Marked seen on
    // DISMISS, not on open. The ? button brings it back any time.
    if (!localStorage.getItem(HOWTO_SEEN_KEY)) this.showHowTo = true;

    // House standard: Cinema Roll's new-version auto-reload. Never fires
    // mid-solve - see appUpdate.js's isSafeMomentForReload.
    initUpdateCheck(this.$store);

    if (this.isApproved) this.consumeJoinCode();
  },
  methods: {
    toggleMeasure() { this.measured = this.measured ? '' : viewportLine(); },
    stampHold() {
      this.held = false;
      this.holdTimer = setTimeout(() => { this.held = true; this.toggleMeasure(); }, 550);
    },
    stampRelease() {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = null;
    },
    stampTap() {
      if (this.held) { this.held = false; return; }
      if (this.measured) { this.measured = ''; return; }
      this.refreshing = true;
      forceRefresh();
    },
    dismissHowTo() {
      this.showHowTo = false;
      try { localStorage.setItem(HOWTO_SEEN_KEY, '1'); } catch { /* storage off */ }
    },
    consumeJoinCode() {
      if (this.pendingPuzzle) {
        const date = this.pendingPuzzle;
        this.pendingPuzzle = null;
        window.history.replaceState({}, '', window.location.pathname);
        this.$store.dispatch('openPuzzle', date);
        return;
      }
      if (!this.pendingJoinCode) return;
      const code = this.pendingJoinCode;
      this.pendingJoinCode = null;
      window.history.replaceState({}, '', window.location.pathname);
      this.$store.dispatch('joinRoomByCode', code);
    },
    goHome() {
      this.$store.dispatch('closePuzzle');
    },
  },
};
</script>
